import type {
  TelegramSettingsUpdate,
  TelegramSettingsView,
  TelegramTestResult,
} from "@screener/protocol";
import { isRecord } from "@/lib/guards";
import { DAEMON_REQUEST_TIMEOUT_MS } from "@/lib/request-timeout";
import type { ApiResult } from "./api-result";

/**
 * Клиент настроек Telegram §6.6 запросного канала §7.6 для экрана «Алерты»
 * (design.md решение 6). `updateTelegram` шлёт только переданные ключи
 * (design.md решение 8): `TelegramSettingsUpdate` целиком несёт
 * `#[serde(default)]` на демоне, отсутствующий ключ значит «не менять».
 */

const TELEGRAM_BASE = "/api/telegram";
const TELEGRAM_TEST_URL = "/api/telegram/test";

function isTelegramSettingsView(value: unknown): value is TelegramSettingsView {
  return (
    isRecord(value) &&
    typeof value.bot_token_set === "boolean" &&
    (value.chat_id === null || typeof value.chat_id === "string") &&
    typeof value.min_interval_sec === "number"
  );
}

function isTelegramTestResult(value: unknown): value is TelegramTestResult {
  if (!isRecord(value)) {
    return false;
  }
  switch (value.outcome) {
    case "sent":
    case "not_configured":
      return true;
    case "rejected":
      return typeof value.description === "string";
    case "unavailable":
      return typeof value.reason === "string";
    default:
      return false;
  }
}

async function readErrorStatus(response: Response): Promise<ApiResult<never> | undefined> {
  if (response.status === 401) {
    return { status: "unauthorized" };
  }
  if (response.status === 404) {
    return { status: "not_found" };
  }
  if (response.status === 400 || response.status === 422) {
    return { status: "invalid" };
  }
  if (!response.ok) {
    console.error("запрос настроек telegram отклонён демоном", response.status);
    return { status: "unavailable" };
  }
  return undefined;
}

async function readSettingsResponse(response: Response): Promise<ApiResult<TelegramSettingsView>> {
  const error = await readErrorStatus(response);
  if (error) {
    return error;
  }
  try {
    const body: unknown = await response.json();
    if (!isTelegramSettingsView(body)) {
      return { status: "unavailable" };
    }
    return { status: "ok", value: body };
  } catch {
    return { status: "unavailable" };
  }
}

export async function getTelegram(): Promise<ApiResult<TelegramSettingsView>> {
  let response: Response;
  try {
    response = await fetch(TELEGRAM_BASE, {
      signal: AbortSignal.timeout(DAEMON_REQUEST_TIMEOUT_MS),
    });
  } catch {
    return { status: "unavailable" };
  }
  return readSettingsResponse(response);
}

/** Шлёт в теле только те поля `patch`, что реально переданы. */
export async function updateTelegram(
  patch: Partial<TelegramSettingsUpdate>,
): Promise<ApiResult<TelegramSettingsView>> {
  let response: Response;
  try {
    response = await fetch(TELEGRAM_BASE, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
      signal: AbortSignal.timeout(DAEMON_REQUEST_TIMEOUT_MS),
    });
  } catch {
    return { status: "unavailable" };
  }
  return readSettingsResponse(response);
}

export async function clearTelegram(): Promise<ApiResult<TelegramSettingsView>> {
  let response: Response;
  try {
    response = await fetch(TELEGRAM_BASE, {
      method: "DELETE",
      signal: AbortSignal.timeout(DAEMON_REQUEST_TIMEOUT_MS),
    });
  } catch {
    return { status: "unavailable" };
  }
  return readSettingsResponse(response);
}

export async function testTelegram(): Promise<ApiResult<TelegramTestResult>> {
  let response: Response;
  try {
    response = await fetch(TELEGRAM_TEST_URL, {
      method: "POST",
      signal: AbortSignal.timeout(DAEMON_REQUEST_TIMEOUT_MS),
    });
  } catch {
    return { status: "unavailable" };
  }
  const error = await readErrorStatus(response);
  if (error) {
    return error;
  }
  try {
    const body: unknown = await response.json();
    if (!isTelegramTestResult(body)) {
      return { status: "unavailable" };
    }
    return { status: "ok", value: body };
  } catch {
    return { status: "unavailable" };
  }
}
